import { getDay, parseISO } from "date-fns";
import { CalendarSlot } from "../types";
import { WEEK_DAY_SHORT_LABELS } from "../constants";
import { PSY_SERVICE } from "../../../utils/constants";
import { ClientRow, ReservationInfo } from "./listTypes";

export const getShortName = (fullName: string) => {
    const parts = fullName.trim().split(" ").filter(Boolean);
    if (parts.length === 0) return fullName;
    if (parts.length === 1) return parts[0];
    const firstWord = parts[0];
    const lastWord = parts[parts.length - 1];
    return `${firstWord} ${lastWord}`;
};

export const formatTime = (time: string) => {
    if (!time) return "";
    const parts = time.split(":");
    return `${parts[0]}:${parts[1]}`;
};

export const getShortWeekDayLabel = (weekDay: number) => {
    return WEEK_DAY_SHORT_LABELS[weekDay] ?? String(weekDay);
};

export const buildPsyClientIdSet = (slots: Array<CalendarSlot>): Set<string> => {
    const psyClientIds = new Set<string>();
    slots.forEach((slot) => {
        const serviceId = slot.serviceId ?? PSY_SERVICE;
        if (serviceId === PSY_SERVICE) {
            psyClientIds.add(slot.clientId);
        }
    });
    return psyClientIds;
};

export const createEmptyClientRow = (slot: CalendarSlot): ClientRow => ({
    clientId: slot.clientId,
    clientName: slot.clientName,
    treatmentId: slot.treatmentId,
    treatmentStatus: slot.treatmentStatus,
    missedNfb: false,
    missedPsy: false,
    missedNfbSessions: [],
    missedPsySessions: [],
    psyTherapistName: null,
    psyTherapistAvatar: null,
    clientOwnerName: slot.clientOwnerName ?? null,
    clientOwnerAvatar: slot.clientOwnerAvatar ?? null,
    isWeeklyNfb: true,
    isWeeklyPsy: true,
    nfbSessionsByDate: {},
    psySessionsByDate: {},
    nfbReservations: [],
    psyReservations: [],
});

export const updateClientRowWithSlot = (row: ClientRow, slot: CalendarSlot, isNfb: boolean) => {
    if (!isNfb && slot.therapistName && !row.psyTherapistName) {
        row.psyTherapistName = slot.therapistName;
        row.psyTherapistAvatar = slot.therapistAvatar ?? null;
    }

    if (isNfb && !slot.isWeekly) {
        row.isWeeklyNfb = false;
    } else if (!isNfb && !slot.isWeekly) {
        row.isWeeklyPsy = false;
    }

    if (slot.missedTwoConsecutiveSessions) {
        if (isNfb) {
            row.missedNfb = true;
            if (slot.missedSessions && row.missedNfbSessions.length === 0) {
                row.missedNfbSessions = slot.missedSessions;
            }
        } else {
            row.missedPsy = true;
            if (slot.missedSessions && row.missedPsySessions.length === 0) {
                row.missedPsySessions = slot.missedSessions;
            }
        }
    }

    const reservation: ReservationInfo = { weekDay: slot.weekDay, time: slot.time };
    const reservationsArray = isNfb ? row.nfbReservations : row.psyReservations;
    const existingReservation = reservationsArray.find(
        (r) => r.weekDay === reservation.weekDay && r.time === reservation.time
    );
    if (!existingReservation) {
        reservationsArray.push(reservation);
    }

    const sessionsByDate = isNfb ? row.nfbSessionsByDate : row.psySessionsByDate;
    slot.futureBookedSessions?.forEach((session) => {
        const sessionWeekDay = getDay(parseISO(session.sessionDate));
        sessionsByDate[session.sessionDate] = {
            matches: session.matches,
            time: session.time,
            weekDay: sessionWeekDay,
            bypassedWarningReason: session.bypassedWarningReason,
        };
    });
};

